import { client } from './backend/client'
import router from './router'
import store from './store'

// Where somebody who has just signed out belongs: the sign-in screen. `replace`
// rather than `push`, for the same reason the response interceptor replaces
// rather than pushes -- the back button must not return to a page that needs a
// session and bounce straight back here.
//
// The callback form rather than the promise one, and the callbacks do nothing:
// Vue Router rejects the promise for a navigation to the route already shown, and
// somebody signing out of a session they are not looking at has no failing
// sign-out to be told about. Same reasoning as `signIn`.

function goToSignIn () {
  router.replace({ name: 'signin' }, () => {}, () => {})
}

// Signs out of this device, and only this device.
//
// Client-side on purpose, and it is the control a user reaches for on a shared
// machine. `POST /api/auth/signout` revokes *every* session the account holds --
// the token version lives on the account and has no notion of an individual
// token, so the server cannot revoke the session it was called with and leave the
// rest alone (see `Metalist.Accounts.revoke_sessions/1`). Calling it here would
// sign a person out of their phone because they used a library computer, which is
// the complaint the global control exists to solve rather than the thing to do
// quietly on their behalf.
//
// So this one talks to nobody. The token is dropped from the store and from local
// storage and the visitor is sent to the sign-in screen, and every other device
// carries on with the session it already had. The trade-off is stated rather than
// hidden: this token is not revoked, so it would still work if it were somehow
// read back out of this browser. What closes that is not a client-side
// instruction -- it is the browser's own storage being cleared, and the expiry on
// the token. Revoking it is what the other control is for.
export function signOutHere (): void {
  store.commit('sessionCleared')
  goToSignIn()
}

// A failure the user can be shown. Carries the underlying error as its `cause`,
// so nothing is lost by the message being for a person.
export class SignOutEverywhereFailed extends Error {
  constructor (cause: unknown) {
    super('You could not be signed out of your other devices. Please try again.')
    this.name = 'SignOutEverywhereFailed'
    ;(this as any).cause = cause
  }
}

// Signs out of every device the account is signed in on, including this one.
//
// The explicitly chosen control, and separate from `signOutHere` rather than a
// variant of it, because it is not the same action: this one asks the server to
// move the account's token version on, which invalidates every token it ever
// issued. A user who suspects somebody else has their token needs that; a user
// leaving a shared machine does not, and the two are labelled differently in the
// menu for exactly that reason.
//
// Clears the session only after the server has answered `204`. The other order
// would sign this device out and then discover it had revoked nothing, which is
// the one outcome both worse than either: the user is told they are signed out
// everywhere and they are not. A failure therefore leaves the session in place
// and reports itself, and the user can try again or sign out of this device alone
// -- which is the fallback the separate control exists to be.
export async function signOutEverywhere (): Promise<void> {
  try {
    await client.post('/auth/signout')
  } catch (error) {
    // A `401` has already been answered by the response interceptor, which
    // cleared the session and asked for a sign-in -- the token was no good, so
    // there was nothing to revoke and no session left to keep. Reporting a
    // failure on top of that would tell a user whose session had already ended
    // that the server could not be reached.
    if ((error as any).response !== undefined && (error as any).response.status === 401) {
      return
    }

    throw new SignOutEverywhereFailed(error)
  }

  store.commit('sessionCleared')
  goToSignIn()
}
